// Product-language formatting (PRD §4.3). The helpers are `@modelcurve/grammar`'s, so the app, activity
// lines and MCP summaries say things the same way. Only the adapters to the app's view model live here.

import { linkSentence as grammarLinkSentence, slugHandle, type NodeNames } from "@modelcurve/grammar/browser";
import { linkToWire } from "@/api/adapt";
import type { ModelNode, Link } from "@/model/types";

export {
	analystGloss,
	fmtInput,
	fmtMonths,
	fmtShort,
	fmtValue,
	fmtX,
	opWord,
	parseAmount,
	pct,
	points,
	singleThreshold,
	stepBands,
	stepWord,
} from "@modelcurve/grammar/browser";

export function title(nodes: Record<string, ModelNode>, id: string): string {
	return nodes[id]?.title ?? id;
}

function names(nodes: Record<string, ModelNode>): NodeNames {
	const out: NodeNames = {};
	for (const [h, n] of Object.entries(nodes)) out[h] = n.kind === "metric" ? { title: n.title, unit: n.unit } : { title: n.title };
	return out;
}

/** The sentence for a link that is not saved yet (the link dialog's preview). Saved links carry the API's `sentence`. */
export function linkSentence(l: Link, nodes: Record<string, ModelNode>): string {
	return grammarLinkSentence(linkToWire(l), names(nodes));
}

/** The handle the API would give a new node with this title (the API adds a suffix when it is taken). */
export function previewHandle(title: string, taken: Record<string, unknown>): string {
	const base = slugHandle(title);
	let h = base;
	for (let k = 2; taken[h]; k++) h = `${base}_${k}`;
	return h;
}

/**
 * Changes made by a program for a person: an AI app connected over MCP (`mcp`), or an API token (`api`,
 * the command-line tool or a script). Both are shown with the client's name, which for a token is the
 * name the person gave it.
 */
export function byClient(origin: string | null | undefined): boolean {
	return origin === "mcp" || origin === "api";
}

/** "Claude Code: " before an activity line made by a program, or "" for a person's own change. */
export function clientPrefix(origin: string | null | undefined, clientName: string | null | undefined): string {
	if (!byClient(origin)) return "";
	return `${clientName || (origin === "api" ? "An API token" : "Claude")}: `;
}
